/**
 * CreateCreditNoteModal
 * Modal for creating a credit note - includes invoice search and selection
 */

import React, { useState, useEffect } from 'react';
import { Dialog, Transition, RadioGroup } from '@headlessui/react';
import {
  XMarkIcon,
  DocumentMinusIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ArrowLeftIcon,
  DocumentTextIcon,
  ArrowDownTrayIcon,
  PaperAirplaneIcon,
  EyeIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { useCreateCreditNote, useNextCreditNoteNumber, useFinalizeCreditNote } from '../../hooks/useCreditNotes';
import { downloadCreditNotePdf } from '../../api/facturation.api';
import { useInvoices } from '../../hooks/useInvoices';
import { getInvoiceById } from '../../api/facturation.api';
import type { Invoice, InvoiceLine, CreditNoteType, CreateCreditNoteData, InvoiceFilters } from '../../types/facturation.types';
import { SearchModeSelector, type SearchMode } from '../shared';
import { useAppStore } from '@/app/store';
import { VAT_RATES_PERCENT } from '../../constants/vat.constants';
import { calculateLineTotals, calculateLineTotalsFromTvac } from '../../utils/currency';
import { ModalErrorBoundary } from '../ErrorBoundary';
import { PdfPreviewModal } from '../PdfPreviewModal/PdfPreviewModal';
import { formatDate } from '../../utils/dateFormat';
import '../../facturation.css';

interface SearchContext {
  mode: 'affaire' | 'client';
  term: string;
}

interface CreateCreditNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  /** Pre-set search context - skips search step and goes directly to invoice selection */
  searchContext?: SearchContext;
}

interface CreditLine {
  originalLigneId?: number;
  invoiceId: number;
  invoiceNumber: string;
  description: string;
  // Unit pricing
  unitPriceHtva: number;      // Price per unit
  unitPriceTvac?: number;     // TVAC price - source of truth for exact calculations
  tvaRate: number;
  // Quantity-based tracking
  originalQuantity: number;   // Original invoice line quantity
  creditedQuantity: number;   // Already credited quantity
  remainingQuantity: number;  // Remaining quantity that can be credited
  // User input
  quantityToCredit: number;   // How many units to credit (user input)
  isFullyCredited: boolean;   // True if all units have been credited
}

// Format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('fr-BE', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
};

export const CreateCreditNoteModal: React.FC<CreateCreditNoteModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  searchContext
}) => {
  // Step: 'search' -> 'select' -> 'form' -> 'generating' -> 'preview' -> 'finalizing' -> 'success' | 'error'
  // If searchContext is provided, skip to 'select'
  const [step, setStep] = useState<'search' | 'select' | 'form' | 'generating' | 'preview' | 'finalizing' | 'success' | 'error'>('search');

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('recent');
  const [searchTerm, setSearchTerm] = useState('');
  const [invoiceFilters, setInvoiceFilters] = useState<InvoiceFilters>({
    page: 1,
    limit: 20,
    sort_by: 'created_at',
    sort_order: 'DESC',
  });
  const [hasSearched, setHasSearched] = useState(false);

  // Selection state
  const [selectedInvoiceIds, setSelectedInvoiceIds] = useState<Set<number>>(new Set());
  const [selectedInvoices, setSelectedInvoices] = useState<Invoice[]>([]);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Form state
  const [creditType, setCreditType] = useState<CreditNoteType>('PARTIAL');
  const [reason, setReason] = useState('');
  const [creditLines, setCreditLines] = useState<CreditLine[]>([]);
  const [expandedInvoices, setExpandedInvoices] = useState<Set<number>>(new Set());
  const [errorMessage, setErrorMessage] = useState('');
  // Custom amount state
  const [customAmountHtva, setCustomAmountHtva] = useState<string>('');
  const [customAmountTvac, setCustomAmountTvac] = useState<string>('');
  const [customDescription, setCustomDescription] = useState<string>('');
  const [customTvaRate, setCustomTvaRate] = useState<number>(VAT_RATES_PERCENT.STANDARD);

  // Generated credit note state
  const [generatedCreditNote, setGeneratedCreditNote] = useState<{
    id: number;
    credit_note_number: string | null;
    total_tvac: number;
    pdf_path?: string;
    email_sent?: boolean;
    email_recipient?: string | null;
    email_error?: string | null;
  } | null>(null);

  // PDF preview and email state (matching invoice modal behavior)
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [isPdfPreviewMode, setIsPdfPreviewMode] = useState(false);
  const [sendEmailOnFinalize, setSendEmailOnFinalize] = useState(true);
  const [isFinalizingFalco, setIsFinalizingFalco] = useState(false);

  // Hooks
  const { data: nextNumber } = useNextCreditNoteNumber();
  const createMutation = useCreateCreditNote();
  const finalizeMutation = useFinalizeCreditNote();
  const { data: invoicesData, isLoading: isLoadingInvoices } = useInvoices(
    hasSearched ? invoiceFilters : { page: 1, limit: 0 }
  );
  const { addNotification } = useAppStore();

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      // Reset all selection state
      setSelectedInvoiceIds(new Set());
      setSelectedInvoices([]);
      setIsLoadingDetails(false);

      // Reset all form state
      setCreditType('PARTIAL');
      setReason('');
      setCreditLines([]);
      setExpandedInvoices(new Set());
      setErrorMessage('');

      // Reset custom amount state
      setCustomAmountHtva('');
      setCustomAmountTvac('');
      setCustomDescription('');
      setCustomTvaRate(VAT_RATES_PERCENT.STANDARD);

      // Reset generated credit note state
      setGeneratedCreditNote(null);

      // Reset PDF preview and email state
      setShowPdfPreview(false);
      setIsPdfPreviewMode(false);
      setSendEmailOnFinalize(true);
      setIsFinalizingFalco(false);

      // If searchContext is provided, skip search step and go directly to selection
      if (searchContext) {
        setSearchMode(searchContext.mode);
        setSearchTerm(searchContext.term);
        setInvoiceFilters({
          page: 1,
          limit: 25,
          sort_by: 'created_at',
          sort_order: 'DESC',
          search: searchContext.term,
        });
        setHasSearched(true);
        setStep('select');
      } else {
        // Reset search state
        setStep('search');
        setSearchMode('recent');
        setSearchTerm('');
        setInvoiceFilters({
          page: 1,
          limit: 20,
          sort_by: 'created_at',
          sort_order: 'DESC',
        });
        setHasSearched(false);
      }
    }
  }, [isOpen, searchContext]);

  // Initialize credit lines when invoices are loaded
  useEffect(() => {
    if (selectedInvoices && selectedInvoices.length > 0) {
      const allLines: CreditLine[] = [];
      const expanded = new Set<number>();

      selectedInvoices.forEach((invoice) => {
        expanded.add(invoice.id);
        if (invoice.lines) {
          invoice.lines.forEach((line: InvoiceLine) => {
            // Use quantity-based tracking
            const originalQuantity = line.quantity;
            const creditedQuantity = line.credited_quantity || 0;
            const remainingQuantity = Math.max(0, originalQuantity - creditedQuantity);
            // Fully credited when no remaining quantity (allow small rounding for decimals)
            const isFullyCredited = remainingQuantity < 0.0001;

            // Skip fully credited lines entirely - don't show them in the list
            if (isFullyCredited) {
              return; // Skip this line
            }

            allLines.push({
              originalLigneId: line.id,
              invoiceId: invoice.id,
              invoiceNumber: invoice.invoice_number || 'Brouillon',
              description: line.description,
              // Unit pricing
              unitPriceHtva: line.unit_price_htva,
              unitPriceTvac: line.unit_price_tvac,  // TVAC for exact calculations
              tvaRate: line.tva_rate,
              // Quantity-based tracking
              originalQuantity,
              creditedQuantity,
              remainingQuantity,
              // Default: credit all remaining units
              quantityToCredit: remainingQuantity,
              isFullyCredited: false,  // Already filtered out above
            });
          });
        }
      });

      setCreditLines(allLines);
      setExpandedInvoices(expanded);
    }
  }, [selectedInvoices]);

  // Handle search
  const handleSearch = () => {
    if (searchMode === 'recent') {
      setInvoiceFilters({
        page: 1,
        limit: 20,
        sort_by: 'created_at',
        sort_order: 'DESC',
        search: undefined,
      });
    } else if (searchTerm.trim()) {
      setInvoiceFilters({
        page: 1,
        limit: 25,
        sort_by: 'created_at',
        sort_order: 'DESC',
        search: searchTerm.trim(),
      });
    }
    setHasSearched(true);
    setStep('select');
  };

  // Handle mode change
  const handleModeChange = (mode: SearchMode) => {
    setSearchMode(mode);
    setSearchTerm('');
  };

  // Toggle invoice selection
  const toggleInvoiceSelection = (invoiceId: number) => {
    setSelectedInvoiceIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  // Proceed to form with selected invoices
  const handleProceedToForm = async () => {
    if (selectedInvoiceIds.size === 0) return;

    setIsLoadingDetails(true);
    try {
      const fullInvoices = await Promise.all(
        Array.from(selectedInvoiceIds).map(id => getInvoiceById(id))
      );
      setSelectedInvoices(fullInvoices);
      setStep('form');
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
      setErrorMessage('Erreur lors du chargement des factures');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  // Go back to search (only available when no searchContext)
  const handleBackToSearch = () => {
    if (searchContext) {
      // If we have a search context, just close the modal
      onClose();
    } else {
      setStep('search');
      setHasSearched(false);
      setSelectedInvoiceIds(new Set());
    }
  };

  // Go back to selection
  const handleBackToSelect = () => {
    setStep('select');
  };

  // Toggle invoice expansion
  const toggleInvoiceExpanded = (invoiceId: number) => {
    setExpandedInvoices(prev => {
      const newSet = new Set(prev);
      if (newSet.has(invoiceId)) {
        newSet.delete(invoiceId);
      } else {
        newSet.add(invoiceId);
      }
      return newSet;
    });
  };

  // Update quantity to credit for a line
  const updateLineQuantity = (index: number, quantity: number) => {
    setCreditLines(prev =>
      prev.map((line, i) => {
        if (i !== index) return line;
        // Clamp quantity between 0 and remaining
        const clampedQty = Math.max(0, Math.min(quantity, line.remainingQuantity));
        return { ...line, quantityToCredit: clampedQty };
      })
    );
  };

  // Set line to credit all remaining or none
  const toggleAllLinesForInvoice = (invoiceId: number, creditAll: boolean) => {
    setCreditLines(prev =>
      prev.map(line =>
        line.invoiceId === invoiceId
          ? { ...line, quantityToCredit: creditAll ? line.remainingQuantity : 0 }
          : line
      )
    );
  };

  // Calculate totals - based on quantities to credit
  // TVAC PRECISION: When unit_price_tvac is available (from tarif import), use it
  // as source of truth and derive TVA as TVAC - HTVA to avoid rounding errors.
  const linesToCredit = creditLines.filter(l => l.quantityToCredit > 0);
  const totalHtva = linesToCredit.reduce((sum, l) => sum + (l.quantityToCredit * l.unitPriceHtva), 0);
  const totalTvac = linesToCredit.reduce((sum, l) => {
    const lineHtva = l.quantityToCredit * l.unitPriceHtva;
    // Use TVAC if available for exact calculations
    if (l.unitPriceTvac) {
      return sum + (l.quantityToCredit * l.unitPriceTvac);
    }
    // Fallback for legacy data without stored TVAC
    return sum + lineHtva + (lineHtva * l.tvaRate / 100);
  }, 0);
  const totalTva = totalTvac - totalHtva;  // Derive TVA for exactness


  // Calculate custom amount totals. When the user typed a TVAC amount, TVAC is the
  // source of truth (exact TVA via TVAC − HTVA); otherwise HTVA drives the totals.
  const customHtvaInput = parseFloat(customAmountHtva) || 0;
  const customTvacInput = parseFloat(customAmountTvac) || 0;
  const customLineTotals = customTvacInput > 0
    ? calculateLineTotalsFromTvac(1, customTvacInput, customTvaRate)
    : calculateLineTotals(1, customHtvaInput, customTvaRate);
  const customHtva = customLineTotals.lineHtva;
  const customTva = customLineTotals.lineTva;
  const customTvac = customLineTotals.lineTvac;

  // Bidirectional HTVA/TVAC sync (mirrors the invoice composer).
  const handleCustomHtvaChange = (value: string) => {
    setCustomAmountHtva(value);
    const htva = parseFloat(value);
    setCustomAmountTvac(
      Number.isFinite(htva) ? (Math.round(htva * (1 + customTvaRate / 100) * 100) / 100).toString() : ''
    );
  };
  const handleCustomTvacChange = (value: string) => {
    setCustomAmountTvac(value);
    const tvac = parseFloat(value);
    // Keep HTVA full precision so the TVAC round-trips exactly.
    setCustomAmountHtva(Number.isFinite(tvac) ? String(tvac / (1 + customTvaRate / 100)) : '');
  };
  const handleCustomRateChange = (rate: number) => {
    setCustomTvaRate(rate);
    const htva = parseFloat(customAmountHtva);
    if (Number.isFinite(htva)) {
      setCustomAmountTvac((Math.round(htva * (1 + rate / 100) * 100) / 100).toString());
    }
  };

  // Group lines by invoice
  const linesByInvoice = selectedInvoices.map(invoice => ({
    invoice,
    lines: creditLines.filter(l => l.invoiceId === invoice.id)
  }));

  // Get invoice affaire display
  const getInvoiceAffaire = (invoice: Invoice): string => {
    if (invoice.affaires && invoice.affaires.length > 0) {
      return invoice.affaires.map(a => a.k_id_dossier || a.affaire_id).join(', ');
    }
    return '-';
  };

  const handleCreate = async () => {
    if (!reason.trim()) {
      setErrorMessage('Veuillez indiquer une raison');
      return;
    }

    if (creditType === 'CUSTOM' && (!customAmountHtva || customHtva <= 0)) {
      setErrorMessage('Veuillez indiquer un montant valide');
      return;
    }

    if (creditType === 'CUSTOM' && !customDescription.trim()) {
      setErrorMessage('Veuillez indiquer une description pour le montant');
      return;
    }

    setStep('generating');
    try {
      // Build lines based on credit type
      let lines: CreateCreditNoteData['lines'];

      if (creditType === 'PARTIAL') {
        // Collect all lines with quantity to credit
        lines = creditLines
          .filter(l => l.quantityToCredit > 0)
          .map(l => ({
            originalLigneId: l.originalLigneId,
            invoiceId: l.invoiceId,
            description: l.description,
            quantity: l.quantityToCredit,  // Credit the specified quantity
            unitPriceHtva: l.unitPriceHtva,  // Original unit price
            unitPriceTvac: l.unitPriceTvac,  // TVAC for exact calculations
            tvaRate: l.tvaRate
          }));
      } else if (creditType === 'CUSTOM') {
        lines = [{
          description: customDescription.trim(),
          quantity: 1,
          unitPriceHtva: customHtva,
          // Send TVAC when the user entered it → backend uses it as source of truth.
          unitPriceTvac: customTvacInput > 0 ? customTvacInput : undefined,
          tvaRate: customTvaRate
        }];
      }

      // Create a single credit note linked to all selected invoices
      const data: CreateCreditNoteData = {
        invoiceIds: selectedInvoices.map(inv => inv.id),
        creditType,
        reason: reason.trim(),
        lines
      };

      const result = await createMutation.mutateAsync(data);

      // Store generated credit note and go to preview step
      setGeneratedCreditNote({
        id: result.id,
        credit_note_number: result.credit_note_number,
        total_tvac: result.total_tvac,
        pdf_path: result.pdf_path || undefined
      });
      setStep('preview');
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue');
    }
  };

  // Handle finalize (send to Falco)
  const handleFinalize = async () => {
    if (!generatedCreditNote) return;

    setIsFinalizingFalco(true);
    setStep('finalizing');
    try {
      const result = await finalizeMutation.mutateAsync({
        id: generatedCreditNote.id,
        sendEmail: sendEmailOnFinalize
      });

      // Update generated credit note with validated data
      // The result is the CreditNote with email_sent, email_recipient, email_error fields
      if (result) {
        const creditNoteResult = result as {
          id: number;
          credit_note_number: string | null;
          total_tvac: number;
          pdf_path?: string;
          email_sent?: boolean;
          email_recipient?: string | null;
          email_error?: string | null;
        };
        setGeneratedCreditNote({
          ...generatedCreditNote,
          ...creditNoteResult,
          email_sent: creditNoteResult.email_sent ?? false,
          email_recipient: creditNoteResult.email_recipient ?? null,
          email_error: creditNoteResult.email_error ?? null
        });
      }

      setStep('success');
      onSuccess?.();
    } catch (error: unknown) {
      setStep('error');
      setErrorMessage(error instanceof Error ? error.message : 'Une erreur est survenue lors de l\'envoi');
    } finally {
      setIsFinalizingFalco(false);
    }
  };

  // Handle PDF download
  const handleDownloadPdf = async () => {
    if (!generatedCreditNote) return;

    try {
      const blob = await downloadCreditNotePdf(generatedCreditNote.id);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = generatedCreditNote.credit_note_number
        ? `note_credit_${generatedCreditNote.credit_note_number.replace(/\//g, '-')}.pdf`
        : `note_credit_brouillon_${generatedCreditNote.id}.pdf`;
      a.click();
      setTimeout(() => window.URL.revokeObjectURL(url), 100); // Delay for download to start
    } catch (error) {
      console.error('Download failed:', error);
      addNotification({
        type: 'error',
        title: 'Erreur de téléchargement',
        message: 'Impossible de télécharger le PDF. Veuillez réessayer.'
      });
    }
  };

  const canCreate = reason.trim().length > 0 && (
    (creditType === 'PARTIAL' && linesToCredit.length > 0) ||
    (creditType === 'CUSTOM' && customHtva > 0 && customDescription.trim().length > 0)
  );
  const isMultipleInvoices = selectedInvoices.length > 1;

  return (
    <ModalErrorBoundary>
    <Transition show={isOpen} as={React.Fragment}>
      <Dialog
        onClose={onClose}
        className="relative z-50"
        // Focus trap is automatically enabled by HeadlessUI Dialog
      >
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white shadow-xl transition-all">
                {/* Header - Focus trap is automatically handled by HeadlessUI Dialog */}
                <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {(step === 'select' || step === 'form') && (
                      <button
                        onClick={step === 'form' ? handleBackToSelect : handleBackToSearch}
                        className="p-1 text-gray-500 hover:text-gray-700 rounded-lg hover:bg-gray-100"
                      >
                        <ArrowLeftIcon className="h-5 w-5" />
                      </button>
                    )}
                    <DocumentMinusIcon className="h-6 w-6 text-red-600" />
                    <div>
                      <Dialog.Title className="text-lg font-semibold text-gray-900">
                        {step === 'search' && 'Nouvelle note de crédit'}
                        {step === 'select' && 'Sélectionner les factures'}
                        {step === 'form' && 'Créer la note de crédit'}
                        {step === 'generating' && 'Génération en cours...'}
                        {step === 'preview' && 'Vérifier la note de crédit'}
                        {step === 'finalizing' && 'Envoi en cours...'}
                        {step === 'success' && 'Note de crédit envoyée'}
                        {step === 'error' && 'Erreur'}
                      </Dialog.Title>
                      {step === 'select' && searchContext && (
                        <p className="text-sm text-gray-500">
                          {searchContext.mode === 'affaire'
                            ? `Affaire: ${searchContext.term}`
                            : `Client: ${searchContext.term}`
                          }
                        </p>
                      )}
                      {step === 'form' && selectedInvoices.length > 0 && (
                        <p className="text-sm text-gray-500">
                          {isMultipleInvoices
                            ? `${selectedInvoices.length} factures sélectionnées`
                            : `Facture ${selectedInvoices[0]?.invoice_number}`
                          }
                        </p>
                      )}
                      {step === 'preview' && generatedCreditNote && (
                        <p className="text-sm text-gray-500">
                          {generatedCreditNote.credit_note_number}
                        </p>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="facturation-btn-modal-close"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                </div>

                {/* Content */}
                <div className="p-6 max-h-[60vh] overflow-y-auto">
                  {/* Step 1: Search */}
                  {step === 'search' && (
                    <div className="space-y-6">
                      <div className="text-center mb-6">
                        <p className="text-gray-500">Recherchez les factures pour lesquelles créer une note de crédit</p>
                      </div>

                      <SearchModeSelector
                        mode={searchMode}
                        onModeChange={handleModeChange}
                        searchTerm={searchTerm}
                        onSearchChange={setSearchTerm}
                        onSearch={handleSearch}
                        isLoading={false}
                      />

                      {searchMode === 'recent' && (
                        <div className="fact-search-mode-action">
                          <button
                            onClick={handleSearch}
                            className="fact-search-mode-action__btn"
                          >
                            Voir les 20 dernieres factures
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Step 2: Select invoices */}
                  {step === 'select' && (
                    <div className="space-y-4">
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-600">
                          {selectedInvoiceIds.size} facture(s) sélectionnée(s)
                        </span>
                      </div>

                      <div className="border border-gray-200 rounded-lg max-h-80 overflow-auto">
                        {isLoadingInvoices ? (
                          <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                          </div>
                        ) : !invoicesData?.invoices || invoicesData.invoices.filter(inv => inv.status !== 'CANCELLED' && !inv.is_fully_credited).length === 0 ? (
                          <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                            <DocumentTextIcon className="w-12 h-12 text-gray-300 mb-3" />
                            <p className="text-sm">Aucune facture trouvee</p>
                            <p className="text-xs text-gray-400 mt-1">Les factures annulees ou entierement creditees ne sont pas affichees</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-gray-100">
                            {invoicesData.invoices.filter(inv => inv.status !== 'CANCELLED' && !inv.is_fully_credited).map((invoice) => (
                              <div
                                key={invoice.id}
                                onClick={() => toggleInvoiceSelection(invoice.id)}
                                className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors flex items-center gap-3 ${
                                  selectedInvoiceIds.has(invoice.id) ? 'bg-primary/5 border-l-4 border-primary' : ''
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={selectedInvoiceIds.has(invoice.id)}
                                  onChange={() => {}}
                                  className="h-4 w-4 text-primary rounded border-gray-300"
                                  aria-label={`Sélectionner facture ${invoice.invoice_number}`}
                                />
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono text-sm font-medium text-gray-900">
                                      {invoice.invoice_number}
                                    </span>
                                    <span className={`px-1.5 py-0.5 rounded text-xs ${
                                      invoice.status === 'PAID' ? 'bg-green-100 text-green-800' :
                                      invoice.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                                      'bg-gray-100 text-gray-800'
                                    }`}>
                                      {invoice.status}
                                    </span>
                                    {invoice.total_credited && invoice.total_credited > 0 && (
                                      <span className="px-1.5 py-0.5 rounded text-xs bg-orange-100 text-orange-800">
                                        Partiellement credite
                                      </span>
                                    )}
                                  </div>
                                  <p className="text-sm text-gray-600 truncate">{invoice.billing_name}</p>
                                  <div className="text-xs text-gray-500">
                                    Affaire: {getInvoiceAffaire(invoice)} | {formatDate(invoice.invoice_date)}
                                  </div>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {formatCurrency(invoice.total_tvac)}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Step 3: Form */}
                  {step === 'form' && (
                    <div className="space-y-6">
                      {/* Original invoices info */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">
                          {isMultipleInvoices ? 'Factures originales' : 'Facture originale'}
                        </h4>
                        <div className="space-y-2">
                          {selectedInvoices.map(invoice => (
                            <div key={invoice.id} className="flex justify-between text-sm text-gray-600 py-1 border-b border-gray-100 last:border-b-0">
                              <div>
                                <span className="font-mono font-medium">{invoice.invoice_number}</span>
                                <span className="mx-2">-</span>
                                <span>{invoice.billing_name}</span>
                              </div>
                              <span className="font-medium">{formatCurrency(invoice.total_tvac)}</span>
                            </div>
                          ))}
                          {isMultipleInvoices && (
                            <div className="flex justify-between text-sm font-semibold text-gray-900 pt-2 border-t border-gray-200">
                              <span>Total</span>
                              <span>{formatCurrency(selectedInvoices.reduce((sum, inv) => sum + inv.total_tvac, 0))}</span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Credit type selection */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Type de note de crédit
                        </label>
                        <RadioGroup value={creditType} onChange={setCreditType}>
                          <div className="grid grid-cols-2 gap-3">
                            <RadioGroup.Option
                              value="PARTIAL"
                              className={({ checked }) =>
                                `cursor-pointer rounded-lg border p-4 ${
                                  checked
                                    ? 'border-primary bg-primary/5'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`
                              }
                            >
                              {({ checked }) => (
                                <div>
                                  <div className={`font-medium ${checked ? 'text-primary' : 'text-gray-900'}`}>
                                    Annulation partielle
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    Sélectionner les lignes
                                  </div>
                                </div>
                              )}
                            </RadioGroup.Option>
                            <RadioGroup.Option
                              value="CUSTOM"
                              className={({ checked }) =>
                                `cursor-pointer rounded-lg border p-4 ${
                                  checked
                                    ? 'border-primary bg-primary/5'
                                    : 'border-gray-200 hover:bg-gray-50'
                                }`
                              }
                            >
                              {({ checked }) => (
                                <div>
                                  <div className={`font-medium ${checked ? 'text-primary' : 'text-gray-900'}`}>
                                    Montant libre
                                  </div>
                                  <div className="text-sm text-gray-500 mt-1">
                                    Saisir un montant
                                  </div>
                                </div>
                              )}
                            </RadioGroup.Option>
                          </div>
                        </RadioGroup>
                      </div>

                      {/* Custom amount input */}
                      {creditType === 'CUSTOM' && (
                        <div className="space-y-4 bg-gray-50 rounded-lg p-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Description *
                            </label>
                            <input
                              type="text"
                              value={customDescription}
                              onChange={(e) => setCustomDescription(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
                              placeholder="Ex: Remise commerciale, correction de facturation..."
                            />
                          </div>
                          <div className="grid grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Montant HTVA *
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={customAmountHtva}
                                  onChange={(e) => handleCustomHtvaChange(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary pr-12"
                                  placeholder="0.00"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">EUR</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Montant TVAC
                              </label>
                              <div className="relative">
                                <input
                                  type="number"
                                  step="0.01"
                                  min="0"
                                  value={customAmountTvac}
                                  onChange={(e) => handleCustomTvacChange(e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary pr-12"
                                  placeholder="0.00"
                                />
                                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">EUR</span>
                              </div>
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Taux TVA
                              </label>
                              <select
                                value={customTvaRate}
                                onChange={(e) => handleCustomRateChange(Number(e.target.value))}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
                              >
                                <option value={VAT_RATES_PERCENT.STANDARD}>{VAT_RATES_PERCENT.STANDARD}%</option>
                                <option value={VAT_RATES_PERCENT.REDUCED}>{VAT_RATES_PERCENT.REDUCED}%</option>
                                <option value={VAT_RATES_PERCENT.SUPER_REDUCED}>{VAT_RATES_PERCENT.SUPER_REDUCED}%</option>
                                <option value={VAT_RATES_PERCENT.ZERO}>{VAT_RATES_PERCENT.ZERO}%</option>
                              </select>
                            </div>
                          </div>
                          {customHtva > 0 && (
                            <div className="text-sm text-gray-600 pt-2 border-t border-gray-200">
                              <div className="flex justify-between">
                                <span>TVA ({customTvaRate}%):</span>
                                <span>{formatCurrency(customTva)}</span>
                              </div>
                              <div className="flex justify-between font-medium">
                                <span>Total TVAC:</span>
                                <span>{formatCurrency(customTvac)}</span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Partial: line selection grouped by invoice */}
                      {creditType === 'PARTIAL' && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Lignes à créditer
                          </label>
                          <div className="border border-gray-200 rounded-lg overflow-hidden">
                            {linesByInvoice.map(({ invoice, lines }) => {
                              const isExpanded = expandedInvoices.has(invoice.id);
                              const linesWithCredit = lines.filter(l => l.quantityToCredit > 0);
                              // Calculate total TVAC for lines being credited (use stored TVAC for exactness)
                              const invoiceTotal = lines.reduce((sum, l) => {
                                const lineHtva = l.quantityToCredit * l.unitPriceHtva;
                                if (l.unitPriceTvac) {
                                  return sum + (l.quantityToCredit * l.unitPriceTvac);
                                }
                                return sum + lineHtva * (1 + l.tvaRate / 100);
                              }, 0);

                              return (
                                <div key={invoice.id} className="border-b border-gray-100 last:border-b-0">
                                  {/* Invoice header */}
                                  <div
                                    className="flex items-center justify-between px-4 py-3 bg-gray-50 cursor-pointer hover:bg-gray-100"
                                    onClick={() => toggleInvoiceExpanded(invoice.id)}
                                  >
                                    <div className="flex items-center gap-2">
                                      {isExpanded ? (
                                        <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                                      ) : (
                                        <ChevronRightIcon className="h-4 w-4 text-gray-500" />
                                      )}
                                      <span className="font-mono font-medium text-sm">{invoice.invoice_number}</span>
                                      <span className="text-xs text-gray-500">
                                        ({linesWithCredit.length}/{lines.length} lignes)
                                      </span>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="text-sm font-medium text-gray-900">
                                        {formatCurrency(invoiceTotal)}
                                      </span>
                                      <div className="flex gap-1">
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleAllLinesForInvoice(invoice.id, true);
                                          }}
                                          className="text-xs text-primary hover:underline"
                                        >
                                          Tout
                                        </button>
                                        <span className="text-gray-300">|</span>
                                        <button
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            toggleAllLinesForInvoice(invoice.id, false);
                                          }}
                                          className="text-xs text-gray-500 hover:underline"
                                        >
                                          Aucun
                                        </button>
                                      </div>
                                    </div>
                                  </div>

                                  {/* Invoice lines */}
                                  {isExpanded && (
                                    <div className="divide-y divide-gray-50">
                                      {lines.map((line, lineIndex) => {
                                        const globalIndex = creditLines.findIndex(l =>
                                          l.invoiceId === invoice.id && l.originalLigneId === line.originalLigneId
                                        );
                                        // Calculate line total TVAC for the quantity to credit (use stored TVAC for exactness)
                                        const lineHtva = line.quantityToCredit * line.unitPriceHtva;
                                        const lineTotal = line.unitPriceTvac
                                          ? line.quantityToCredit * line.unitPriceTvac
                                          : lineHtva * (1 + line.tvaRate / 100);
                                        const isInteger = Number.isInteger(line.remainingQuantity);

                                        return (
                                          <div
                                            key={lineIndex}
                                            className="flex items-center gap-3 px-4 py-3 pl-10"
                                          >
                                            {/* Quantity input */}
                                            <div className="flex items-center gap-1 flex-shrink-0">
                                              <input
                                                type="number"
                                                min="0"
                                                max={line.remainingQuantity}
                                                step={isInteger ? "1" : "0.01"}
                                                value={line.quantityToCredit}
                                                onChange={(e) => updateLineQuantity(globalIndex, parseFloat(e.target.value) || 0)}
                                                className="w-16 px-2 py-1 text-sm border border-gray-300 rounded text-center focus:ring-primary focus:border-primary"
                                                aria-label={`Quantité à créditer pour ${line.description}`}
                                              />
                                              <span className="text-xs text-gray-500">
                                                / {isInteger ? line.remainingQuantity : line.remainingQuantity.toFixed(2)}
                                              </span>
                                            </div>
                                            <div className="flex-1 min-w-0">
                                              <div className="flex flex-col gap-0.5">
                                                <p className="text-sm text-gray-900">{line.description}</p>
                                                <div className="flex items-center gap-2 text-xs text-gray-500">
                                                  <span>{formatCurrency(line.unitPriceHtva)}/unité</span>
                                                  {line.creditedQuantity > 0 && (
                                                    <span className="px-1.5 py-0.5 rounded bg-orange-100 text-orange-700">
                                                      {line.creditedQuantity} déjà crédité(s)
                                                    </span>
                                                  )}
                                                </div>
                                              </div>
                                            </div>
                                            <div className="text-sm font-medium text-gray-700 whitespace-nowrap">
                                              {line.quantityToCredit > 0 ? formatCurrency(lineTotal) : '-'}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Reason */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Raison de l'annulation *
                        </label>
                        <textarea
                          value={reason}
                          onChange={(e) => setReason(e.target.value)}
                          rows={3}
                          aria-label="Raison de l'annulation"
                          aria-required="true"
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-primary focus:border-primary"
                          placeholder="Indiquez la raison de la note de crédit..."
                        />
                      </div>

                      {/* Credit amount summary */}
                      <div className="bg-red-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-red-900 mb-3">Montant à créditer</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between text-red-700">
                            <span>HTVA:</span>
                            <span>-{formatCurrency(
                              creditType === 'CUSTOM' ? customHtva : totalHtva
                            )}</span>
                          </div>
                          <div className="flex justify-between text-red-700">
                            <span>TVA:</span>
                            <span>-{formatCurrency(
                              creditType === 'CUSTOM' ? customTva : totalTva
                            )}</span>
                          </div>
                          <div className="flex justify-between font-semibold text-red-900 border-t border-red-200 pt-2">
                            <span>TVAC:</span>
                            <span>-{formatCurrency(
                              creditType === 'CUSTOM' ? customTvac : totalTvac
                            )}</span>
                          </div>
                        </div>
                        {isMultipleInvoices && (
                          <p className="text-xs text-red-700 mt-3">
                            Cette note de crédit sera liée à {selectedInvoices.length} factures
                          </p>
                        )}
                      </div>

                      {/* Next credit note number */}
                      {nextNumber && (
                        <div className="text-sm text-gray-600">
                          Numero: <span className="font-mono font-medium">{nextNumber}</span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Generating state */}
                  {step === 'generating' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-gray-600">
                        Génération de la note de crédit et du PDF...
                      </p>
                    </div>
                  )}

                  {/* Preview state - PDF generated, ready to validate */}
                  {step === 'preview' && generatedCreditNote && (
                    <div className="space-y-6">
                      {/* Draft warning - when no credit note number yet */}
                      {!generatedCreditNote.credit_note_number && (
                        <div className="fact-draft-warning">
                          <p>
                            <strong>Brouillon</strong> - Le numero de note de credit sera attribue lors de la validation.
                          </p>
                        </div>
                      )}

                      <div className="bg-green-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <CheckCircleIcon className="h-6 w-6 text-green-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-green-900">
                              {generatedCreditNote.credit_note_number ? 'Note de credit generee' : 'Brouillon cree'}
                            </h3>
                            <p className="text-sm text-green-700 mt-1">
                              {generatedCreditNote.credit_note_number ? (
                                <>La note de credit <span className="font-mono font-medium">{generatedCreditNote.credit_note_number}</span> a ete generee.</>
                              ) : (
                                <>Le brouillon a ete cree. Verifiez le PDF avant de valider.</>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Credit note summary */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Resume</h4>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Numero:</span>
                            <span className="font-mono font-medium">{generatedCreditNote.credit_note_number || 'Brouillon'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Montant TVAC:</span>
                            <span className="font-medium text-red-600">-{formatCurrency(generatedCreditNote.total_tvac)}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Facture(s) liee(s):</span>
                            <span>{selectedInvoices.map(i => i.invoice_number).join(', ')}</span>
                          </div>
                        </div>
                      </div>

                      {/* Email option */}
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={sendEmailOnFinalize}
                            onChange={(e) => setSendEmailOnFinalize(e.target.checked)}
                            disabled={isFinalizingFalco}
                            className="fact-email-checkbox"
                          />
                          <span className="text-sm text-gray-700">
                            Envoyer la note de credit par email au client
                          </span>
                        </label>
                        {selectedInvoices[0]?.billing_email && (
                          <p className="text-xs text-gray-500 mt-1 ml-7">
                            Email: {selectedInvoices[0].billing_email}
                          </p>
                        )}
                      </div>

                      {/* PDF preview */}
                      <div className="flex justify-center">
                        {generatedCreditNote.pdf_path ? (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(false);
                              setShowPdfPreview(true);
                            }}
                            disabled={isFinalizingFalco}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Voir le PDF
                          </button>
                        ) : (
                          <button
                            onClick={() => {
                              setIsPdfPreviewMode(true);
                              setShowPdfPreview(true);
                            }}
                            disabled={isFinalizingFalco}
                            className="inline-flex items-center gap-2 px-4 py-2 bg-amber-100 text-amber-800 rounded-lg hover:bg-amber-200 transition-colors disabled:opacity-50"
                          >
                            <EyeIcon className="h-5 w-5" />
                            Previsualiser le brouillon
                          </button>
                        )}
                      </div>

                      {/* Next step info */}
                      <div className="bg-blue-50 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <PaperAirplaneIcon className="h-6 w-6 text-blue-600 flex-shrink-0" />
                          <div>
                            <h3 className="font-medium text-blue-900">Prochaine etape</h3>
                            <p className="text-sm text-blue-700 mt-1">
                              Verifiez le PDF puis cliquez sur "Valider et envoyer" pour transmettre la note de credit a Falco.
                              {selectedInvoices[0]?.billing_email && sendEmailOnFinalize && (
                                <span className="block mt-1">
                                  La note de credit sera egalement envoyee par email a <span className="font-medium">{selectedInvoices[0].billing_email}</span>.
                                </span>
                              )}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Finalizing state */}
                  {step === 'finalizing' && (
                    <div className="text-center py-8">
                      <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
                      <p className="mt-4 text-gray-600">
                        Envoi de la note de crédit à Falco...
                      </p>
                    </div>
                  )}

                  {/* Success state */}
                  {step === 'success' && (
                    <div className="text-center py-8">
                      <CheckCircleIcon className="h-12 w-12 text-green-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">
                        Note de credit envoyee
                      </h3>
                      <p className="mt-2 text-gray-600">
                        La note de credit {generatedCreditNote?.credit_note_number || ''} a ete envoyee a Falco avec succes
                        {isMultipleInvoices && ` (liee a ${selectedInvoices.length} factures)`}.
                      </p>

                      {/* Email status */}
                      {generatedCreditNote?.email_sent ? (
                        <div className="mt-4 mx-auto max-w-md bg-green-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 justify-center">
                            <EnvelopeIcon className="h-5 w-5 text-green-600" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-green-800">Email envoye</p>
                              <p className="text-xs text-green-600">
                                La note de credit a ete envoyee a {generatedCreditNote?.email_recipient}
                              </p>
                            </div>
                          </div>
                        </div>
                      ) : generatedCreditNote?.email_error ? (
                        <div className="mt-4 mx-auto max-w-md bg-amber-50 rounded-lg p-3">
                          <div className="flex items-center gap-2 justify-center">
                            <ExclamationTriangleIcon className="h-5 w-5 text-amber-600" />
                            <div className="text-left">
                              <p className="text-sm font-medium text-amber-800">Email non envoye</p>
                              <p className="text-xs text-amber-600">{generatedCreditNote?.email_error}</p>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {generatedCreditNote?.pdf_path && (
                        <button
                          onClick={handleDownloadPdf}
                          className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
                        >
                          <ArrowDownTrayIcon className="h-5 w-5" />
                          Telecharger le PDF
                        </button>
                      )}
                    </div>
                  )}

                  {/* Error state */}
                  {step === 'error' && (
                    <div className="text-center py-8">
                      <ExclamationTriangleIcon className="h-12 w-12 text-red-600 mx-auto" />
                      <h3 className="mt-4 text-lg font-semibold text-gray-900">Erreur</h3>
                      <p className="mt-2 text-red-600">{errorMessage}</p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
                  {step === 'search' && (
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-cancel"
                    >
                      Annuler
                    </button>
                  )}
                  {step === 'select' && (
                    <>
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-cancel"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleProceedToForm}
                        disabled={selectedInvoiceIds.size === 0 || isLoadingDetails}
                        className="facturation-btn-primary"
                      >
                        {isLoadingDetails ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                            Chargement...
                          </>
                        ) : (
                          `Continuer (${selectedInvoiceIds.size})`
                        )}
                      </button>
                    </>
                  )}
                  {step === 'form' && (
                    <>
                      <button
                        onClick={onClose}
                        className="facturation-btn-modal-cancel"
                      >
                        Annuler
                      </button>
                      <button
                        onClick={handleCreate}
                        disabled={!canCreate}
                        className="facturation-btn-modal-danger"
                      >
                        Générer la note de crédit
                      </button>
                    </>
                  )}
                  {step === 'preview' && (
                    <>
                      <button
                        onClick={onClose}
                        disabled={isFinalizingFalco}
                        className="facturation-btn-modal-cancel disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Fermer
                      </button>
                      <button
                        onClick={handleFinalize}
                        disabled={isFinalizingFalco}
                        className="facturation-btn-modal-primary inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isFinalizingFalco ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            Envoi en cours...
                          </>
                        ) : (
                          <>
                            <PaperAirplaneIcon className="h-4 w-4" />
                            Valider et envoyer a Falco
                          </>
                        )}
                      </button>
                    </>
                  )}
                  {(step === 'success' || step === 'error') && (
                    <button
                      onClick={onClose}
                      className="facturation-btn-modal-primary"
                    >
                      Fermer
                    </button>
                  )}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>

    {/* PDF Preview Modal */}
    {showPdfPreview && generatedCreditNote && (
      <PdfPreviewModal
        document={{
          type: 'creditNote',
          id: generatedCreditNote.id,
          number: generatedCreditNote.credit_note_number || 'Brouillon',
          date: new Date().toISOString().split('T')[0],
          clientName: selectedInvoices[0]?.billing_name || '-',
          amount: generatedCreditNote.total_tvac,
          status: generatedCreditNote.credit_note_number ? 'SENT' : 'DRAFT',
          hasPdf: !!generatedCreditNote.pdf_path || isPdfPreviewMode,
        }}
        onClose={() => {
          setShowPdfPreview(false);
          setIsPdfPreviewMode(false);
        }}
        onDownload={handleDownloadPdf}
        previewMode={isPdfPreviewMode}
        onBack={() => {
          setShowPdfPreview(false);
          setIsPdfPreviewMode(false);
        }}
        backLabel="Retour a la validation"
      />
    )}
    </ModalErrorBoundary>
  );
};

export default CreateCreditNoteModal;
